import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppBar from '../../../../components/AppBar';
import AppPaths from '../../../../AppPaths';
import {
  Badge,
  Button as MantineButton,
  Drawer,
  Group,
  List,
  NavLink,
  Paper,
  Radio,
  Stack,
  Text,
  Textarea,
  Modal,
  Box,
  Anchor,
} from '@mantine/core';
import { Link, useHistory, useLocation } from 'react-router-dom';
import BackIcon from '../../../../assets/images/back-button.svg';
import DimmedX from '../../../../assets/images/icons/close_dimmed.svg';
import { Image } from '../../../../components/Image';
import {
  PurchaseSubscriptionContextProvider,
  usePurchaseSubscriptionContext,
} from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import { SubscriptionOption } from '../../../../components/SubscriptionOptionDetails';
import { gqlBillingCadenceToBillingCadence } from '../../../../lib/subscription';
import {
  getBillingCadenceDescription,
  getShortSubscriptionDescription,
  getShortSubscriptionName,
} from '../../util/helpers';
import Button from '../../../../components/Button';
import { DateTime } from 'luxon';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { gqlTypes } from '../../../../types';
import { Species, zendeskCancellationReason } from '../../../../types/gql-op-types';
import { useMutation } from '@apollo/client';
import {
  allSubscriptionsForCurrentUserQuery,
  cancelMembershipMutation,
  claimClickToCancelDiscountMutation,
  claimFreeMonthMutation,
  convertFreeTrialMutation,
  pauseMembershipMutation,
  changeMembershipPlanEndOfTermMutation,
} from '../../../../graphql-operations';
import { notifications } from '@mantine/notifications';
import {
  centsToDollarsNumber,
  expectUnreachable,
  formattedCentsToDollars,
  getLocalizedPricing,
  mkError,
} from '../../../../lib/util';
import { logInternalError } from '../../../../lib/errors';
import { useDispatch } from 'react-redux';
import { cartActions } from '../../../../reducers/subscriptionCart';
import { getDomainConfig } from '../../../../lib/domainConfig';
import { webSubscriptionManage as events } from '../../../../lib/analytics/events';
import { useForm } from '@mantine/form';
import { PHONE_SUPPORT_NUMBER } from '../../../../lib/util/phoneSupport';
import { useZendeskCancellationTicket } from '../../Collar/hooks/useZendesk';
import { SubscriptionCategory } from '../../Collar/util/types';
import { BaseManageSubscriptionState } from '../util/types';
import { fiCurrencyToGQL, useLocale } from '../../../../contexts/LocaleContext';
import { ConvertTrialConfirmContent } from '../ConvertTrialConfirmContent';

// This is a dummy code for staying on the free trial. When modifying a subscription that's
// on the free trial, their option's aren't to actually switch between plans. Their options
// are to continue their free trial or convert to paying, which keeps them on the same plan
// and just ends the trial. This option is to stay on the free trial (i.e. do nothing).
const FREE_TRIAL_CURRENT = 'FREE_TRIAL_CURRENT';

enum CancelDrawerPage {
  InitialCancelReason,
  TechCancelReason,
  MoreDetails,
  CXContact,
  ConfirmCancel,
}

interface CancelFormValues {
  cancelReason: gqlTypes.zendeskCancellationReason | '';
  techCancelReason: gqlTypes.zendeskCancellationReason | '';
  otherReasonText: string;
  issueDetailsText: string;
}

function ModifyContent() {
  const { t } = useTranslation();
  const { device } = usePurchaseSubscriptionContext();
  const currentSubscriptionOption = device.subscription?.subscriptionOption;
  const currentSubscriptionSku = currentSubscriptionOption?.sku ?? '';
  const petId = device.pet?.id;
  const pendingChange = device.subscription?.pendingChanges?.subscriptionOption;
  const isFreeTrial = device.subscription?.hasFreeTrialPlan && device.subscription?.isInTrialPeriod;
  const [selectedSubscriptionSku, setSelectedSubscriptionSku] = useState(
    isFreeTrial ? FREE_TRIAL_CURRENT : currentSubscriptionSku,
  );
  const [confirmedCancelReason, setConfirmedCancelReason] = useState(false);
  const [confirmedPause, setConfirmedPause] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [openedPause, { open: openPause, close: closePause }] = useDisclosure(false);
  const [openedChangePlanConfirm, { open: openChangePlanConfirm, close: closeChangePlanConfirm }] =
    useDisclosure(false);
  const [openedConvertTrial, { open: openConvertTrial, close: closeConvertTrial }] = useDisclosure(false);
  const history = useHistory();
  const location = useLocation<BaseManageSubscriptionState>();
  const dispatch = useDispatch();
  const [cancelDrawerPage, setCancelDrawerPage] = useState<CancelDrawerPage>(CancelDrawerPage.InitialCancelReason);
  const { country, currency, currencyFormatter } = useLocale();
  const isMini = device.currentCollarModel?.toLowerCase() === 'mini';

  const cancelForm = useForm<CancelFormValues>({
    initialValues: {
      cancelReason: '',
      techCancelReason: '',
      otherReasonText: '',
      issueDetailsText: '',
    },
    validateInputOnChange: true,
    // validate per-page pattern from https://v6.mantine.dev/form/recipes/#form-with-multiple-steps
    validate: (values) => {
      if (cancelDrawerPage === CancelDrawerPage.InitialCancelReason) {
        return {
          otherReasonText:
            values.cancelReason === gqlTypes.zendeskCancellationReason.OTHER && values.otherReasonText.length < 10
              ? t('reason_must_be_at_least_10_characters')
              : null,
        };
      } else if (cancelDrawerPage === CancelDrawerPage.MoreDetails) {
        return {
          issueDetailsText: values.issueDetailsText.length < 10 ? t('please_input_at_least_10_characters') : null,
        };
      }
      return {};
    },
  });

  const currentIsMonthly = currentSubscriptionOption?.renewalMonths === 1;
  const canPause =
    currentIsMonthly && !!device.shouldOfferPauseMonthDuringCancellation && !device.subscription?.isPaused;
  const canCancel = device.canCancel;
  const canCancelDateTime = DateTime.fromISO(device.canCancelDate);
  const offerFreeMonth = device.shouldOfferFreeMonthDuringCancellation ?? false;
  const offerDiscount = device.shouldOfferDiscountDuringClickToCancel ?? false;
  const currentPriceString = currentSubscriptionOption
    ? formattedCentsToDollars(currentSubscriptionOption?.priceInCents)
    : '';

  const applicableSubscriptionProducts: SubscriptionOption[] = useMemo(() => {
    if (device.hasSubscriptionOverride) {
      return []; // No plan change options for an override subscription.
    }

    const result: SubscriptionOption[] = [];
    for (const option of device.purchasableSubscriptionOptions) {
      // filter out 3 month plan if the user is not subscribed to it
      if (option.renewalMonths === 3 && currentSubscriptionOption?.renewalMonths !== 3) {
        continue;
      }
      result.push({
        ...option,
        billingCadence: gqlBillingCadenceToBillingCadence(option.billingCadence),
      });
    }

    // return subscription options sorted so that the longest term is first
    return result.sort((a, b) => b.renewalMonths - a.renewalMonths);
  }, [device, currentSubscriptionOption]);

  const selectedSubscriptionOption = useMemo(() => {
    return applicableSubscriptionProducts.find((option) => option.sku === selectedSubscriptionSku);
  }, [applicableSubscriptionProducts, selectedSubscriptionSku]);

  const planRadioStyles = (theme: any) => ({
    root: {
      padding: '20px 24px',
      borderRadius: 24,
      border: `2px solid ${theme.colors['background-light-tertiary'][0]}`,
    },
    body: {
      gap: 8,
    },
    labelWrapper: {
      flexGrow: 1,
    },
    inner: {
      alignSelf: 'center',
    },
  });

  const freeTrialPlanRadioOptions = isFreeTrial
    ? [
        ...(applicableSubscriptionProducts.length > 0
          ? [
              <Radio
                label={
                  <Text fz={24} fw={600}>
                    {getShortSubscriptionName(t, applicableSubscriptionProducts[0].renewalMonths)}
                  </Text>
                }
                value={applicableSubscriptionProducts[0].sku}
                labelPosition="left"
                key={applicableSubscriptionProducts[0].sku}
                color="dark"
                description={
                  <Text fz={16} fw={600} color="type-light-secondary">
                    {getBillingCadenceDescription(
                      getLocalizedPricing(applicableSubscriptionProducts[0].pricingInCents, currency),
                      applicableSubscriptionProducts[0].renewalMonths,
                      currencyFormatter,
                    )}{' '}
                    {t('plus_waived_activation_fee')}
                  </Text>
                }
                styles={planRadioStyles}
              />,
            ]
          : []),
        <Radio
          label={
            <Text fz={24} fw={600}>
              {t('free_trial_label')}
            </Text>
          }
          value={FREE_TRIAL_CURRENT}
          labelPosition="left"
          key={FREE_TRIAL_CURRENT}
          color="dark"
          description={
            <Text fz={16} fw={600} color="type-light-secondary">
              {t('n_days_free', { count: 30 })}
            </Text>
          }
          styles={planRadioStyles}
        />,
      ]
    : null;

  const defaultPlanRadioOptions = applicableSubscriptionProducts.map((p) => (
    <Radio
      label={
        <Text fz={24} fw={600}>
          {getShortSubscriptionName(t, p.renewalMonths)}
        </Text>
      }
      value={p.sku}
      labelPosition="left"
      key={p.sku}
      color="dark"
      description={
        <Text fz={16} fw={600} color="type-light-secondary">
          {getBillingCadenceDescription(
            getLocalizedPricing(p.pricingInCents, currency),
            p.renewalMonths,
            currencyFormatter,
          )}
        </Text>
      }
      styles={planRadioStyles}
    />
  ));

  const planRadioOptions = freeTrialPlanRadioOptions ?? defaultPlanRadioOptions;

  const petSpecies = device.pet?.species;
  const allCancelReasons = [
    {
      content: t('device_or_app_isnt_working_as_expected'),
      value: gqlTypes.zendeskCancellationReason.COLLAR_DOES_NOT_WORK_PROPERLY,
    },
    {
      content: t('the_device_is_physically_damaged_or_broken'),
      value: gqlTypes.zendeskCancellationReason.COLLAR_IS_PHYSICALLY_BROKEN,
    },
    {
      content: t('there_was_a_billing_issue'),
      value: gqlTypes.zendeskCancellationReason.BILLING_ERROR,
    },
    petSpecies === Species.DOG
      ? {
          content: t('my_dog_passed_away'),
          value: gqlTypes.zendeskCancellationReason.DOG_PASSED_AWAY,
        }
      : {
          content: t('my_cat_passed_away'),
          value: gqlTypes.zendeskCancellationReason.CAT_PASSED_AWAY,
        },
    ...(petSpecies === Species.CAT
      ? [
          {
            content: t('my_cat_doesnt_want_to_wear_it'),
            value: gqlTypes.zendeskCancellationReason.CAT_WILL_NOT_WEAR_COLLAR,
          },
        ]
      : []),
    {
      content: t('the_subscription_is_too_expensive'),
      value: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE,
    },
    ...(isMini
      ? [
          {
            content: t('i_lost_my_device'),
            value: gqlTypes.zendeskCancellationReason.COLLAR_IS_LOST,
          },
        ]
      : []),
    {
      content: t('im_switching_to_another_fi_device'),
      value: gqlTypes.zendeskCancellationReason.REPLACING_DEVICE,
    },
    {
      content: t('im_canceling_temporarily_and_plan_on_reactivating'),
      value: gqlTypes.zendeskCancellationReason.PLAN_TO_REACTIVATE,
    },
    {
      content: t('other_issue'),
      value: gqlTypes.zendeskCancellationReason.OTHER,
    },
  ];

  const freeTrialCancelReasonValues = new Set([
    gqlTypes.zendeskCancellationReason.COLLAR_DOES_NOT_WORK_PROPERLY,
    gqlTypes.zendeskCancellationReason.COLLAR_IS_PHYSICALLY_BROKEN,
    gqlTypes.zendeskCancellationReason.BILLING_ERROR,
    gqlTypes.zendeskCancellationReason.DOG_PASSED_AWAY,
    gqlTypes.zendeskCancellationReason.CAT_PASSED_AWAY,
    gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE,
    gqlTypes.zendeskCancellationReason.OTHER,
  ]);

  const cancelReasons = isFreeTrial
    ? allCancelReasons.filter((r) => freeTrialCancelReasonValues.has(r.value))
    : allCancelReasons;

  const techCancelReasons = [
    {
      content: t('the_app_doesnt_work'),
      value: gqlTypes.zendeskCancellationReason.COLLAR_DOES_NOT_WORK_PROPERLY,
    },
    {
      content: t('the_battery_drains_too_quickly'),
      value: gqlTypes.zendeskCancellationReason.BATTERY_DRAINS_TOO_QUICKLY,
    },
    {
      content: t('my_dogs_location_isnt_accurate'),
      value: gqlTypes.zendeskCancellationReason.LOCATION_REPORTS_INCORRECT,
    },
  ];

  const cancelRadioOptions = cancelReasons.map((r) => (
    <Radio
      value={r.value}
      key={r.value}
      label={
        <Text fz={16} fw={500}>
          {r.content}
        </Text>
      }
      color="dark"
    />
  ));

  const techCancelRadioOptions = techCancelReasons.map((r) => (
    <Radio
      value={r.value}
      key={r.value}
      label={
        <Text fz={16} fw={500}>
          {r.content}
        </Text>
      }
      color="dark"
    />
  ));

  const petName = device.pet?.name ?? 'your pet';
  const endDateText = DateTime.fromISO(device.subscription?.currentTermEndsAt).toFormat('MMMM d, y');
  const subscriptionResumeDateText = DateTime.fromISO(device.subscription?.currentTermEndsAt)
    .plus({ months: 1 })
    .toFormat('MMMM d, y');
  const showOffer =
    !isFreeTrial &&
    cancelForm.values.cancelReason !== zendeskCancellationReason.DOG_PASSED_AWAY &&
    ((offerFreeMonth && currentIsMonthly) || (offerDiscount && !currentIsMonthly)) &&
    device.currentCollarModel?.toLowerCase() !== 'mini' &&
    cancelForm.values.cancelReason !== zendeskCancellationReason.REPLACING_DEVICE &&
    cancelForm.values.cancelReason !== zendeskCancellationReason.PLAN_TO_REACTIVATE;

  const [cancelMutation, cancelMutationState] = useMutation<
    gqlTypes.ECOMMERCE_cancelMembership,
    gqlTypes.ECOMMERCE_cancelMembershipVariables
  >(cancelMembershipMutation, {
    refetchQueries: [{ query: allSubscriptionsForCurrentUserQuery }],
    onCompleted: (data) => {
      const success = data.cancelMembership?.success ?? false;
      if (!success) {
        notifications.show({
          title: t('failed_to_cancel_membership'),
          message: t('please_try_again_later_or_contact_support'),
          color: 'red',
          autoClose: false,
        });
        logInternalError(`Failed to cancel membership - ${data.cancelMembership?.reasonForFailure}`);
      } else {
        // TODO: styling, checkmark icon
        notifications.show({
          message: t('thank_you_for_your_feedback'),
          color: 'green',
        });

        events.modify.cancelCompleted({
          petId: petId,
          cancellationReason: cancelForm.values.cancelReason,
          otherReasonText: cancelForm.values.otherReasonText,
          currentSku: currentSubscriptionSku,
          confirmedCancelReason,
          shownOffer: showOffer && applicableOffer !== undefined,
        });

        // navigate back to home page
        history.push({
          pathname: AppPaths.WebSubscription.Root,
          search: window.location.search,
          state: location.state,
        });
      }
    },
    onError: (err) => {
      logInternalError(err);
    },
  });

  const [claimFreeMonth, claimFreeMonthState] = useMutation<
    gqlTypes.ECOMMERCE_claimFreeMonth,
    gqlTypes.ECOMMERCE_claimFreeMonthVariables
  >(claimFreeMonthMutation, {
    refetchQueries: [{ query: allSubscriptionsForCurrentUserQuery }],
    onCompleted: (data) => {
      const success = data.claimFreeMonth?.success ?? false;
      if (!success) {
        notifications.show({
          title: t('failed_to_claim_free_month'),
          message: t('please_try_again_later_or_contact_support'),
          color: 'red',
          autoClose: false,
        });
        logInternalError(`Failed to claim free month`);
      } else {
        // todo: styling, checkmark icon
        notifications.show({
          message: t('renewal_offer_applied'),
          color: 'green',
        });
        events.modify.retentionFreeMonthSuccess({
          petId: petId,
          cancellationReason: cancelForm.values.cancelReason,
          otherReasonText: cancelForm.values.otherReasonText,
          currentSku: currentSubscriptionSku,
          confirmedCancelReason,
          shownOffer: showOffer && applicableOffer !== undefined,
        });

        // navigate back to home page
        history.push({
          pathname: AppPaths.WebSubscription.Root,
          search: window.location.search,
          state: location.state,
        });
      }
    },
    onError: (err) => {
      logInternalError(err);
    },
  });

  const [claimDiscount, claimDiscountState] = useMutation<
    gqlTypes.ECOMMERCE_claimClickToCancelDiscount,
    gqlTypes.ECOMMERCE_claimClickToCancelDiscountVariables
  >(claimClickToCancelDiscountMutation, {
    refetchQueries: [{ query: allSubscriptionsForCurrentUserQuery }],
    onCompleted: (data) => {
      const success = data.claimClickToCancelDiscount?.success ?? false;
      if (!success) {
        notifications.show({
          title: t('failed_to_claim_discount'),
          message: t('please_try_again_later_or_contact_support'),
          color: 'red',
          autoClose: false,
        });
        logInternalError(`Failed to claim discount: ${data.claimClickToCancelDiscount?.reasonForFailure}`);
      } else {
        // TODO: styling, checkmark icon
        notifications.show({
          message: t('renewal_offer_applied'),
          color: 'green',
        });
        events.modify.retentionDiscountSuccess({
          petId: petId,
          cancellationReason: cancelForm.values.cancelReason,
          otherReasonText: cancelForm.values.otherReasonText,
          currentSku: currentSubscriptionSku,
          confirmedCancelReason,
          shownOffer: showOffer && applicableOffer !== undefined,
        });

        // navigate back to home page
        history.push({
          pathname: AppPaths.WebSubscription.Root,
          search: window.location.search,
          state: location.state,
        });
      }
    },
    onError: (err) => {
      logInternalError(err);
    },
  });

  const [pauseMutation, pauseMutationState] = useMutation<
    gqlTypes.ECOMMERCE_pauseMembership,
    gqlTypes.ECOMMERCE_pauseMembershipVariables
  >(pauseMembershipMutation, {
    refetchQueries: [{ query: allSubscriptionsForCurrentUserQuery }],
    onCompleted: (data) => {
      const success = data.pauseMembership?.success ?? false;
      if (!success) {
        notifications.show({
          title: t('failed_to_pause_membership'),
          message: t('please_try_again_later_or_contact_support'),
          color: 'red',
          autoClose: false,
        });
        logInternalError(`Failed to pause membership: ${data.pauseMembership?.reasonForFailure}`);
      } else {
        setConfirmedPause(true);
        events.modify.pauseCompleted({ petId: petId, currentSku: currentSubscriptionSku });
      }
    },
    onError: (err) => {
      notifications.show({
        title: t('failed_to_pause_membership'),
        message: t('please_try_again_later_or_contact_support'),
        color: 'red',
        autoClose: false,
      });
      logInternalError(err);
    },
  });

  const [changePlanMutation, changePlanMutationState] = useMutation<
    gqlTypes.ECOMMERCE_changeMembershipPlanEndOfTerm,
    gqlTypes.ECOMMERCE_changeMembershipPlanEndOfTermVariables
  >(changeMembershipPlanEndOfTermMutation, {
    refetchQueries: [{ query: allSubscriptionsForCurrentUserQuery }],
    onCompleted: (data) => {
      const success = data.changeMembershipPlanEndOfTerm?.success ?? false;
      if (!success) {
        notifications.show({
          title: t('failed_to_change_membership'),
          message: t('please_try_again_later_or_contact_support'),
          color: 'red',
          autoClose: false,
        });
        logInternalError(`Failed to change membership: ${data.changeMembershipPlanEndOfTerm?.reasonForFailure}`);
      } else {
        notifications.show({
          message: t('your_membership_has_been_updated'),
          color: 'green',
        });
        closeChangePlanConfirm();
        events.modify.updateMembershipSuccess({
          petId: petId,
          currentSku: currentSubscriptionSku,
          newSku: selectedSubscriptionSku,
        });

        // navigate back to home page
        history.push({
          pathname: AppPaths.WebSubscription.Root,
          search: window.location.search,
          state: location.state,
        });
      }
    },
    onError: (err) => {
      notifications.show({
        title: t('failed_to_change_membership'),
        message: t('please_try_again_later_or_contact_support'),
        color: 'red',
        autoClose: false,
      });
      logInternalError(err);
    },
  });

  const [convertTrialMutation, convertTrialMutationState] = useMutation<
    gqlTypes.ECOMMERCE_convertFreeTrial,
    gqlTypes.ECOMMERCE_convertFreeTrialVariables
  >(convertFreeTrialMutation, {
    refetchQueries: [{ query: allSubscriptionsForCurrentUserQuery }],
    onCompleted: (data) => {
      const success = data.convertFreeTrial?.success ?? false;
      if (!success) {
        notifications.show({
          title: t('something_went_wrong'),
          message: t('please_try_again_later_or_contact_support'),
          color: 'red',
          autoClose: false,
        });
        logInternalError(`Failed to convert free trial: ${data.convertFreeTrial?.reasonForFailure}`);
      } else {
        notifications.show({
          message: t('your_membership_has_been_updated'),
          color: 'green',
        });
        closeConvertTrial();
        history.push({
          pathname: AppPaths.WebSubscription.Root,
          search: window.location.search,
          state: location.state,
        });
      }
    },
    onError: (err) => {
      notifications.show({
        title: t('something_went_wrong'),
        message: t('please_try_again_later_or_contact_support'),
        color: 'red',
        autoClose: false,
      });
      logInternalError(err);
    },
  });

  const { mutation: zendeskMutation } = useZendeskCancellationTicket({
    analyticsEvent: events.modify.createdCXSupportTicket,
    selectedCancelReason:
      cancelForm.values.cancelReason !== '' ? cancelForm.values.cancelReason : gqlTypes.zendeskCancellationReason.OTHER,
    device: device,
    subscriptionCategory: currentIsMonthly ? SubscriptionCategory.MONTHLY : SubscriptionCategory.PREPAID,
    redirectAfterSuccess: false,
  });

  const offerMutationState = currentIsMonthly ? claimFreeMonthState : claimDiscountState;

  const handleCancelMembership = () => {
    events.modify.canceled({
      petId: petId,
      cancellationReason: cancelForm.values.cancelReason,
      otherReasonText: cancelForm.values.otherReasonText,
      currentSku: currentSubscriptionSku,
      confirmedCancelReason,
      shownOffer: showOffer && applicableOffer !== undefined,
    });
    cancelMutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription?.id ?? '',
        },
      },
    });
  };

  const handleAcceptOffer = () => {
    events.modify.claimedRetentionOffer({
      petId: petId,
      currentSku: currentSubscriptionSku,
      cancellationReason: cancelForm.values.cancelReason,
      otherReasonText: cancelForm.values.otherReasonText,
      confirmedCancelReason,
      shownOffer: showOffer && applicableOffer !== undefined,
    });
    if (currentIsMonthly) {
      claimFreeMonth({
        variables: {
          input: {
            moduleId: device.moduleId,
          },
        },
      });
    } else {
      claimDiscount({
        variables: {
          input: {
            moduleId: device.moduleId,
            recurlySubscriptionId: device.subscription?.id ?? '',
          },
        },
      });
    }
  };

  const handleSelectMembership = (value: string) => {
    setSelectedSubscriptionSku(value);
    if (value !== currentSubscriptionSku && value !== FREE_TRIAL_CURRENT) {
      dispatch(
        cartActions.setCartItem({
          moduleId: device.moduleId,
          sku: value,
          currency,
        }),
      );
    }
  };

  const handleUpdateMembership = () => {
    events.modify.updateMembership({
      petId: petId,
      currentSku: currentSubscriptionSku,
      newSku: selectedSubscriptionSku,
    });
    if (isFreeTrial) {
      openConvertTrial();
    } else {
      openChangePlanConfirm();
    }
  };

  const handleConfirmUpdateMembership = () => {
    events.modify.confirmedUpdateMembership({
      petId: petId,
      currentSku: currentSubscriptionSku,
      newSku: selectedSubscriptionSku,
    });
    changePlanMutation({
      variables: {
        input: {
          moduleId: device.moduleId,
          newSku: selectedSubscriptionSku,
          currency: fiCurrencyToGQL(currency),
          countryCode: country.toString(),
        },
      },
    });
  };

  const handlePause = () => {
    const NUM_TERMS_TO_PAUSE = 1;
    events.modify.confirmedPause({ petId: petId, currentSku: currentSubscriptionSku });
    pauseMutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription?.id ?? '',
          moduleId: device.moduleId,
          numTerms: NUM_TERMS_TO_PAUSE,
        },
      },
    });
  };

  const handlePauseDone = () => {
    history.push({
      pathname: AppPaths.WebSubscription.Root,
      search: window.location.search,
      state: location.state,
    });
  };

  const handleCloseCancel = () => {
    events.modify.closedCancelDrawer({
      petId: petId,
      cancellationReason: cancelForm.values.cancelReason,
      otherReasonText: cancelForm.values.otherReasonText,
      confirmedCancelReason,
      shownOffer: showOffer && applicableOffer !== undefined,
    });
    cancelForm.reset();
    setConfirmedCancelReason(false);
    setCancelDrawerPage(CancelDrawerPage.InitialCancelReason);
    close();
  };

  const handleTechHardwareCancelDone = () => {
    cancelForm.reset();
    setCancelDrawerPage(CancelDrawerPage.InitialCancelReason);
    close();
    history.push({
      pathname: AppPaths.WebSubscription.Root,
      search: window.location.search,
      state: location.state,
    });
  };

  const handleClosePause = () => {
    events.modify.closedPause({ petId: petId, currentSku: currentSubscriptionSku });
    closePause();
  };

  const handleCloseChangePlanConfirm = () => {
    events.modify.closedUpdateMembership({
      petId: petId,
      currentSku: currentSubscriptionSku,
      newSku: selectedSubscriptionSku,
    });
    closeChangePlanConfirm();
  };

  const handleConfirmConvertTrial = () => {
    convertTrialMutation({
      variables: {
        input: {
          subscriptionId: device.subscription?.id ?? '',
        },
      },
    });
  };

  const handleCloseConvertTrial = () => {
    closeConvertTrial();
  };

  // TODO: make this mobile drawer size dynamic to the content
  // https://app.asana.com/1/553833434016848/project/1207101507783044/task/1210644291686459?focus=true
  const drawerSizePx = 626;

  interface Offer {
    renewalMonths: number;
    discount: number;
    description: string;
    isFreeMonth: boolean;
  }
  // NOTE: until we pull in the offers from the backend, these need to be kept in-sync with the coupons
  // that are configured here: https://github.com/barkinglabs/web/blob/14ed5b0238d2092a1bec40ed9339e24e8c5b0362/src/lib/ecommerce/account/subscriptions.ts#L2681-L2696
  // Asana task to improve this: https://app.asana.com/1/553833434016848/project/1207222017559586/task/1210332820650691?focus=true
  const allOffers: Offer[] = [
    {
      renewalMonths: 24,
      discount: 50,
      description: t('enjoy_all_benefits_of_2_year_membership_at_a_discount_and_keep_access_to_pet_name_data', {
        petName,
      }),
      isFreeMonth: false,
    },
    {
      renewalMonths: 12,
      discount: 30,
      description: t('enjoy_all_benefits_of_1_year_membership_at_a_discount_and_keep_access_to_pet_name_data', {
        petName,
      }),
      isFreeMonth: false,
    },
    {
      renewalMonths: 6,
      discount: 15,
      description: t('enjoy_all_benefits_of_6_month_membership_at_a_discount_and_keep_access_to_pet_name_data', {
        petName,
      }),
      isFreeMonth: false,
    },
    {
      renewalMonths: 3,
      discount: 5,
      description: t('enjoy_all_benefits_of_3_month_membership_at_a_discount_and_keep_access_to_pet_name_data', {
        petName,
      }),
      isFreeMonth: false,
    },
    {
      renewalMonths: 1,
      discount: 19,
      description: t('enjoy_all_benefits_of_membership_next_month_for_free_and_keep_access_to_pet_name_data', {
        petName,
      }),
      isFreeMonth: true,
    },
  ];

  const applicableOffer = allOffers.find((o) => o.renewalMonths === currentSubscriptionOption?.renewalMonths);

  const getCancelDetailText = () => {
    if (isFreeTrial) {
      return t('if_you_cancel_now_youll_lose_access_to_data', { petName });
    } else if (canCancel) {
      return t('if_you_cancel_now_youll_still_have_access_to_pet_name_data_until_date', { petName, date: endDateText });
    } else {
      return (
        <>
          {t('your_membership_has_a_6_month_commitment_after_date_you_can_cancel_your_membership', {
            date: canCancelDateTime.toFormat('MMMM d, y'),
          })}{' '}
          <Anchor
            href={`https://${getDomainConfig().supportDomain}/hc/en-us/articles/7048727038995-Fi-Membership-billing`}
            target="_blank"
            rel="noreferrer"
            color="dark"
          >
            {t('learn_more_here')}
          </Anchor>
        </>
      );
    }
  };

  const renderCancelPage = () => {
    switch (cancelDrawerPage) {
      case CancelDrawerPage.InitialCancelReason:
        return (
          <form
            onSubmit={cancelForm.onSubmit((values) => {
              setConfirmedCancelReason(true);
              if (values.cancelReason === zendeskCancellationReason.COLLAR_DOES_NOT_WORK_PROPERLY) {
                setCancelDrawerPage(CancelDrawerPage.TechCancelReason);
              } else if (values.cancelReason === zendeskCancellationReason.COLLAR_IS_PHYSICALLY_BROKEN) {
                setCancelDrawerPage(CancelDrawerPage.MoreDetails);
              } else {
                setCancelDrawerPage(CancelDrawerPage.ConfirmCancel);
              }
              events.modify.confirmedCancelReason({
                petId: petId,
                cancellationReason: values.cancelReason,
                otherReasonText: values.otherReasonText,
                currentSku: currentSubscriptionSku,
              });
            })}
          >
            <Stack spacing={24}>
              <Radio.Group
                label={
                  <Text fz={24} fw={600} pb={32}>
                    {t('why_are_you_canceling')}
                  </Text>
                }
                {...cancelForm.getInputProps('cancelReason')}
              >
                <Stack spacing={8}>{cancelRadioOptions}</Stack>
              </Radio.Group>
              {cancelForm.values.cancelReason === gqlTypes.zendeskCancellationReason.OTHER && (
                <Textarea {...cancelForm.getInputProps('otherReasonText')} radius={16} minRows={3} />
              )}
              <Button
                disabled={cancelForm.values.cancelReason === undefined}
                type="submit"
                loading={cancelMutationState.loading}
              >
                {t('continue_to_cancel')}
              </Button>
            </Stack>
          </form>
        );
      case CancelDrawerPage.ConfirmCancel:
        return (
          <Stack>
            <Text fz={24} fw={600}>
              {cancelForm.values.cancelReason === gqlTypes.zendeskCancellationReason.DOG_PASSED_AWAY
                ? t('were_sorry_for_your_loss')
                : t('are_you_sure')}
            </Text>
            {cancelForm.values.cancelReason === gqlTypes.zendeskCancellationReason.DOG_PASSED_AWAY ? (
              <Text fz={16} fw={600}>
                {t(
                  'we_understand_how_difficult_it_is_to_say_goodbye_to_a_beloved_companion_if_you_have_any_questions_about_your_fi_membership_were_here_for_you_at_phone_number_10am_8pm_et',
                  {
                    phoneNumber: PHONE_SUPPORT_NUMBER,
                  },
                )}
              </Text>
            ) : (
              <>
                <Text fz={16} fw={600}>
                  {t('on_date_youll_lose_access_to_pet_name_data', { date: endDateText, petName })}
                </Text>
                <List
                  icon={<Image image={{ type: 'image', url: DimmedX }} />}
                  fz={14}
                  fw={500}
                  color="type-light-secondary"
                >
                  <List.Item>{t('gps_location_tracking')}</List.Item>
                  <List.Item>{t('walk_history')}</List.Item>
                  <List.Item>{t('activity_and_sleep_data')}</List.Item>
                  <List.Item>{t('behavior_insight_history')}</List.Item>
                </List>
              </>
            )}

            {showOffer && applicableOffer && (
              <>
                <Paper shadow="sm" withBorder radius="lg" py={24} px={16}>
                  <Stack spacing={16}>
                    <div>
                      <Text fz={20} fw={700}>
                        {applicableOffer.isFreeMonth
                          ? t('keep_tracking_and_get_1_month_free')
                          : t('keep_tracking_and_save_amount', { amount: applicableOffer.discount })}
                      </Text>
                      <Text c="type-light-secondary" fz={14} fw={500}>
                        {applicableOffer.description}
                      </Text>
                    </div>
                    <Group spacing={8}>
                      <Group spacing={0}>
                        <Text fz={24} fw={700}>
                          {applicableOffer.isFreeMonth
                            ? '$0'
                            : `$${
                                centsToDollarsNumber(currentSubscriptionOption!.priceInCents) - applicableOffer.discount
                              }`}
                        </Text>
                        <Text td="line-through" color="type-light-secondary" fz={24} fw={700}>
                          {currentPriceString}
                        </Text>
                      </Group>
                      <Badge
                        color="fi-yellow"
                        variant="filled"
                        radius="xs"
                        styles={(theme) => ({
                          inner: {
                            color: 'black',
                          },
                        })}
                      >
                        {t('exclusive_offer')}
                      </Badge>
                    </Group>
                  </Stack>
                </Paper>
                <Button
                  onClick={handleAcceptOffer}
                  loading={offerMutationState.loading}
                  disabled={cancelMutationState.loading}
                >
                  {t('claim_discount')}
                </Button>
              </>
            )}
            <Button
              secondary={showOffer}
              onClick={handleCancelMembership}
              loading={cancelMutationState.loading}
              disabled={offerMutationState.loading}
            >
              {cancelForm.values.cancelReason === gqlTypes.zendeskCancellationReason.DOG_PASSED_AWAY
                ? t('confirm_cancellation')
                : t('yes_cancel_anyways')}
            </Button>
          </Stack>
        );
      case CancelDrawerPage.TechCancelReason:
        return (
          <form
            onSubmit={cancelForm.onSubmit((values) => {
              setCancelDrawerPage(CancelDrawerPage.MoreDetails);
              events.modify.confirmedTechCancelReason({
                petId: petId,
                cancellationReason: values.cancelReason,
                confirmedCancelReason,
                techCancelReason: values.techCancelReason,
                currentSku: currentSubscriptionSku,
              });
            })}
          >
            <Stack spacing={32}>
              <Radio.Group
                label={
                  <Text fz={24} fw={600} pb={32}>
                    {t('can_you_tell_us_more')}
                  </Text>
                }
                {...cancelForm.getInputProps('techCancelReason')}
              >
                <Stack spacing={8}>{techCancelRadioOptions}</Stack>
              </Radio.Group>
              <Button disabled={cancelForm.values.techCancelReason === undefined} type="submit">
                {t('continue')}
              </Button>
            </Stack>
          </form>
        );
      case CancelDrawerPage.MoreDetails:
        return (
          <form
            onSubmit={cancelForm.onSubmit((values) => {
              zendeskMutation({
                variables: {
                  input: {
                    cancellationReason:
                      cancelForm.values.techCancelReason !== ''
                        ? cancelForm.values.techCancelReason
                        : cancelForm.values.cancelReason !== ''
                        ? cancelForm.values.cancelReason
                        : gqlTypes.zendeskCancellationReason.OTHER,
                    recurlySubscriptionId: device.subscription?.id ?? '',
                    additionalText:
                      cancelForm.values.cancelReason ===
                        gqlTypes.zendeskCancellationReason.COLLAR_DOES_NOT_WORK_PROPERLY ||
                      cancelForm.values.cancelReason === gqlTypes.zendeskCancellationReason.COLLAR_IS_PHYSICALLY_BROKEN
                        ? cancelForm.values.issueDetailsText
                        : cancelForm.values.otherReasonText,
                  },
                },
              }).then(
                (result) => {
                  // TODO: lock cancel flow for this device
                  // https://app.asana.com/1/553833434016848/project/1204172374987023/task/1210618943917972?focus=true
                  setCancelDrawerPage(CancelDrawerPage.CXContact);
                },
                (err) => {
                  notifications.show({
                    title: t('something_went_wrong'),
                    message: t('failed_to_create_support_ticket_please_contact_support'),
                    color: 'red',
                    autoClose: false,
                  });
                  logInternalError('Failed to create Zendesk cancellation ticket');
                },
              );
            })}
          >
            <Stack>
              <Text fz={24} fw={600}>
                {t('were_sorry_youre_having_this_issue')}
              </Text>
              <Text fz={16} fw={600}>
                {t('can_you_tell_us_what_happened')}
              </Text>
              <Textarea {...cancelForm.getInputProps('issueDetailsText')} radius={16} minRows={3} />
              <Button type="submit" disabled={cancelForm.values.issueDetailsText === ''}>
                {t('continue')}
              </Button>
            </Stack>
          </form>
        );
      case CancelDrawerPage.CXContact:
        return (
          <Stack spacing={30}>
            <Text fz={24} fw={600}>
              {cancelForm.values.cancelReason === gqlTypes.zendeskCancellationReason.COLLAR_IS_PHYSICALLY_BROKEN
                ? t('you_may_be_eligible_for_a_replacement')
                : t('we_can_help')}
            </Text>
            <Text fz={16} fw={600}>
              {cancelForm.values.cancelReason === gqlTypes.zendeskCancellationReason.COLLAR_DOES_NOT_WORK_PROPERLY
                ? t('wed_love_to_make_things_right_one_of_our_specialists_will_be_in_touch_soon_to_assist_you')
                : t('one_of_our_specialists_will_be_in_touch_soon_to_assist_you')}
            </Text>
            <Button onClick={handleTechHardwareCancelDone}>{t('close')}</Button>
          </Stack>
        );
      default:
        expectUnreachable(cancelDrawerPage);
        return mkError(t('something_went_wrong'));
    }
  };

  const renderPausePage = () => {
    return !confirmedPause ? (
      <Stack>
        <Text fz={24} fw={600}>
          {t('are_you_sure')}
        </Text>
        <Text fz={16} fw={600}>
          {t(
            'this_will_pause_your_access_to_the_fi_app_gps_tracking_and_pet_name_data_on_date_and_skip_billing_for_1_month',
            {
              petName,
              date: endDateText,
            },
          )}
        </Text>
        <Button onClick={handlePause} loading={pauseMutationState.loading}>
          {t('confirm')}
        </Button>
        <Button
          secondary
          onClick={() => {
            events.modify.closedPause({ petId: petId, currentSku: currentSubscriptionSku });
            closePause();
          }}
          disabled={pauseMutationState.loading}
        >
          {t('nevermind')}
        </Button>
      </Stack>
    ) : (
      <Stack>
        <Text fz={24} fw={600}>
          {t('your_membership_has_been_paused_for_1_month')}
        </Text>
        <Text fz={16} fw={500}>
          {t('your_will_have_access_to_the_fi_app_until_date', { date: endDateText })}
        </Text>
        <Text fz={16} fw={500}>
          {t('we_will_skip_your_next_monthly_payment_and_your_billing_will_resume_on_date', {
            date: subscriptionResumeDateText,
          })}
        </Text>
        <Button onClick={handlePauseDone}>{t('done')}</Button>
      </Stack>
    );
  };

  const renderChangePlanConfirmPage = () => {
    if (!selectedSubscriptionOption) {
      return (
        <Stack>
          <Text fz={24} fw={600}>
            {t('uh_oh')}
          </Text>
          <Text fz={16} fw={600}>
            {t('invalid_membership_selected_for_update')}
          </Text>
          <Button onClick={handleCloseChangePlanConfirm}>{t('close')}</Button>
        </Stack>
      );
    }
    return (
      <Stack>
        <Text fz={24} fw={600}>
          {t('confirm_membership_change')}
        </Text>
        <Text fz={16} fw={600}>
          {t('your_membership_will_change_to_a_subscription_membership_when_your_current_cycle_ends_on_date', {
            subscription: getShortSubscriptionDescription(t, selectedSubscriptionOption.renewalMonths),
            date: endDateText,
          })}
        </Text>
        <Button onClick={handleConfirmUpdateMembership} loading={changePlanMutationState.loading}>
          {t('confirm')}
        </Button>
        <Button onClick={handleCloseChangePlanConfirm} tertiary>
          {t('nevermind')}
        </Button>
      </Stack>
    );
  };

  const renderConvertTrialConfirmPage = () => {
    const selectedOption = applicableSubscriptionProducts[0];
    if (!selectedOption) {
      return (
        <Stack>
          <Text fz={24} fw={600}>
            {t('uh_oh')}
          </Text>
          <Text fz={16} fw={600}>
            {t('invalid_membership_selected_for_update')}
          </Text>
          <Button onClick={handleCloseConvertTrial}>{t('close')}</Button>
        </Stack>
      );
    }

    return (
      <ConvertTrialConfirmContent
        priceInCents={getLocalizedPricing(selectedOption.pricingInCents, currency)}
        renewalMonths={selectedOption.renewalMonths}
        onConfirm={handleConfirmConvertTrial}
        onClose={handleCloseConvertTrial}
        loading={convertTrialMutationState.loading}
      />
    );
  };

  const drawerStyles = () => ({
    inner: {
      '.mantine-Drawer-content': {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
      },
    },
    close: {
      height: 32,
      width: 32,
    },
  });

  const largeScreen = useMediaQuery('(min-width: 48em)');

  return (
    <>
      <AppBar />
      {largeScreen ? (
        <Modal
          opened={opened}
          onClose={cancelDrawerPage === CancelDrawerPage.CXContact ? handleTechHardwareCancelDone : handleCloseCancel}
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark', radius: 'xl' }}
          centered
          radius={16}
        >
          <Box px={4} pb={24}>
            {renderCancelPage()}
          </Box>
        </Modal>
      ) : (
        <Drawer
          opened={opened}
          onClose={cancelDrawerPage === CancelDrawerPage.CXContact ? handleTechHardwareCancelDone : handleCloseCancel}
          position="bottom"
          size={drawerSizePx}
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark' }}
          styles={drawerStyles}
        >
          {renderCancelPage()}
        </Drawer>
      )}
      {largeScreen ? (
        <Modal
          opened={openedPause}
          onClose={handleClosePause}
          centered
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark', radius: 'xl' }}
          radius={16}
        >
          <Box px={4} pb={24}>
            {renderPausePage()}
          </Box>
        </Modal>
      ) : (
        // TODO: height?
        <Drawer
          opened={openedPause}
          onClose={handleClosePause}
          position="bottom"
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark' }}
          styles={drawerStyles}
        >
          {renderPausePage()}
        </Drawer>
      )}
      {largeScreen ? (
        <Modal
          opened={openedChangePlanConfirm}
          onClose={handleCloseChangePlanConfirm}
          centered
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark', radius: 'xl' }}
          radius={16}
        >
          {renderChangePlanConfirmPage()}
        </Modal>
      ) : (
        // TODO: height?
        <Drawer
          opened={openedChangePlanConfirm}
          onClose={handleCloseChangePlanConfirm}
          position="bottom"
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark' }}
          styles={drawerStyles}
        >
          {renderChangePlanConfirmPage()}
        </Drawer>
      )}
      {largeScreen ? (
        <Modal
          opened={openedConvertTrial}
          onClose={handleCloseConvertTrial}
          centered
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark', radius: 'xl' }}
          radius={16}
        >
          {renderConvertTrialConfirmPage()}
        </Modal>
      ) : (
        <Drawer
          opened={openedConvertTrial}
          onClose={handleCloseConvertTrial}
          position="bottom"
          size={370}
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark' }}
          styles={drawerStyles}
        >
          {renderConvertTrialConfirmPage()}
        </Drawer>
      )}

      <Stack w={'100%'} maw={668} px={24}>
        <NavLink
          component={Link}
          to={{ pathname: AppPaths.WebSubscription.Root, state: location.state }}
          label={t('back_to_all_memberships')}
          icon={<Image image={{ type: 'image', url: BackIcon }} width={18} height={18} />}
          styles={(theme) => ({
            root: theme.fn.hover({ backgroundColor: 'transparent' }),
          })}
        />
        <Radio.Group value={selectedSubscriptionSku} onChange={handleSelectMembership}>
          <Stack>{planRadioOptions}</Stack>
        </Radio.Group>
        <Button
          disabled={
            isFreeTrial
              ? selectedSubscriptionSku === FREE_TRIAL_CURRENT
              : !pendingChange && selectedSubscriptionSku === currentSubscriptionSku
          }
          onClick={handleUpdateMembership}
        >
          {t('update_membership')}
        </Button>
        <Stack spacing={8}>
          {currentIsMonthly && (
            <Button
              secondary
              onClick={() => {
                events.modify.clickedPause({ petId: petId, currentSku: currentSubscriptionSku });
                openPause();
              }}
              disabled={!canPause}
            >
              {t('pause_membership')}
            </Button>
          )}
          <MantineButton
            variant="white"
            color="type-light-primary"
            radius="xl"
            onClick={() => {
              events.modify.clickedCancel({ petId: petId, currentSku: currentSubscriptionSku });
              open();
            }}
            disabled={!canCancel}
            styles={(theme) => ({
              root: { '&[data-disabled]': { backgroundColor: 'white' } },
            })}
          >
            <Text inherit td="underline">
              {t('cancel_membership')}
            </Text>
          </MantineButton>
          <Text color="type-light-secondary" fz={12} fw={500} align="center">
            {getCancelDetailText()}
          </Text>
        </Stack>
      </Stack>
    </>
  );
}

export default function Modify() {
  return (
    <PurchaseSubscriptionContextProvider>
      <ModifyContent />
    </PurchaseSubscriptionContextProvider>
  );
}
